import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { Space } from "antd";
// Using Tabler Icons instead of Ant Design icons
import Table from "../../../../components/Common/dataTable/index";
import { formatDateTime, formatTimeDuration } from "../../../../utils/dateUtils";
import Badge from "../../../../components/Common/ui/Badge";
import AbsenceProofUploadModal from "./AbsenceProofUploadModal";
import { Absence } from "../../../../types/absenceTypes";
import ActionsDropdown, { Action } from "components/Common/ActionsDropdown";
import UserProfileHeader from "components/Common/ui/UserProfileHeader";
import { getSubjectLabelFromCode } from "utils/schoolUtils";
import { useAbility } from "config/abilityContext";
import { all_routes } from "router/all_routes";
import CustomButton from "components/Common/ui/CustomButton";

interface AbsenceTableProps {
  absences: Absence[];
  role: "pedagogy-director" | "student" | "guardian";
  onUpdateStatus: (absenceId: string, status: "excused" | "unexcused") => void;
  onRefresh?: () => void;
}

const AbsenceTable: React.FC<AbsenceTableProps> = ({ absences, role, onUpdateStatus, onRefresh }) => {
  const isGuardian = role === "guardian";
  const ability = useAbility();
  const { t } = useTranslation();
  const canEdit = ability.can("update", "Absence");

  // State for upload modal
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [selectedAbsence, setSelectedAbsence] = useState<Absence | null>(null);

  // Handle opening the upload modal
  const handleOpenUploadModal = (absence: Absence) => {
    setSelectedAbsence(absence);
    setUploadModalVisible(true);
  };

  // Handle closing the upload modal
  const handleCloseUploadModal = () => {
    setUploadModalVisible(false);
    setSelectedAbsence(null);
  };

  // Handle successful upload
  const handleUploadSuccess = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  const columns = [
    {
      title: t("student_name"),
      dataIndex: "studentName",
      key: "studentName",
      render: (_: any, record: Absence) => (
        <UserProfileHeader
          photo={record.student?.user?.photo}
          firstName={record.student?.user?.firstName || ""}
          lastName={record.student?.user?.lastName || ""}
          subtitle={record.student?.user?.email || ""}
          to={all_routes.studentDetails.replace(":id", record?.student?.id || "") + all_routes.studentDetailsTabs.absences}
          canRedirect={ability.can("getAll", "Absence")}
        />
      )
      ,
      hidden: role !== "pedagogy-director",
    },
    {
      title: t("class"),
      dataIndex: "class",
      key: "class",
      render: (_: any, record: Absence) => record.student?.class?.name || "-",
      hidden: role !== "pedagogy-director",
    },
    {
      title: t("subject"),
      dataIndex: "subject",
      key: "subject",
      render: (_: any, record: Absence) => getSubjectLabelFromCode(record?.subject?.code || "") || "-",
    },
    {
      title: t("teacher"),
      dataIndex: "teacher",
      key: "teacher",
      render: (_: any, record: Absence) => (
        <UserProfileHeader
          photo={record.teacher?.user?.photo}
          firstName={record.teacher?.user?.firstName || ""}
          lastName={record.teacher?.user?.lastName || ""}
        />
      )
    },

    {
      title: t("date"),
      dataIndex: "dateTime",
      key: "dateTime",
      render: (dateTime: string) => formatDateTime(dateTime),
    },
    {
      title: t("duration"),
      dataIndex: "duration",
      key: "duration",
      render: (_: any, record: Absence) => formatTimeDuration(record.duration ?? 0),
    },
    {
      title: t("reason"),
      dataIndex: "reason",
      key: "reason",
      render: (reason: string | undefined | null) => {
        if (!reason || !reason.trim()) {
          return "-";
        }

        const maxLength = 40;
        const isLong = reason.length > maxLength;
        const shortText = isLong ? reason.slice(0, maxLength) + "..." : reason;

        return (
          <span
            title={reason}
            style={{
              display: "inline-block",
              maxWidth: 300,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {shortText}
          </span>
        );
      },
    },

    {
      title: t("status"),
      dataIndex: "status",
      key: "status",
      render: (_: any, record: Absence) => {
        // Check if proof is uploaded
        const hasProof = Boolean(record.proofUrl && record.proofUrl.trim() !== '');
        const status = record.status || 'pending';

        return (
          <Space>
            <Badge
              variant={status === "excused" ? "success" : status === "unexcused" ? "danger" : "warning"}
              text={status}
            />
            {hasProof && (
              <i
                className="ti ti-file"
                style={{ color: "green", fontSize: "16px", cursor: "pointer" }}
                onClick={() => window.open(record.proofUrl, '_blank')}
                title={t("download_proof")}
              />
            )}
          </Space>
        );
      },
    },
    // Actions column for admins
    // Actions column for admins and guardians
    {
      title: t("action"),
      key: "action",
      hidden: !canEdit && !isGuardian,
      render: (_: any, record: Absence) => {
        const status = record.status || "pending";

        // Admin actions
        if (canEdit) {
          const actions: Action[] = [];

          // if “pending”, allow both “excuse” and “unexcuse”
          if (record.status === "pending") {
            actions.push({
              label: t("mark_excused"),
              actionType: "modal",
              onClick: () => onUpdateStatus(record.id!, "excused"),
              icon: "ti ti-check",
            });
            actions.push({
              label: t("mark_unexcused"),
              actionType: "modal",
              onClick: () => onUpdateStatus(record.id!, "unexcused"),
              icon: "ti ti-x",
            });
          }
          // if already “unexcused”, still allow “excuse”
          else if (record.status === "unexcused") {
            actions.push({
              label: t("mark_excused"),
              actionType: "modal",
              onClick: () => onUpdateStatus(record.id!, "excused"),
              icon: "ti ti-check",
            });
          }
          // you could add more cases here…

          // render the three-dots menu
          return actions.length > 0 ? <ActionsDropdown actions={actions} /> : <i className="ti ti-lock" />;
        }

        // Guardian upload logic as before
        if (
          isGuardian &&
          (status === "pending" || status === "unexcused")
        ) {
          const hasProof =
            Boolean(record.proofUrl) && record.proofUrl?.trim() !== "";
          if (!hasProof) {
            return (
              // <Button
              //   type="link"
              //   icon={<i className="ti ti-upload" />}
              //   className="text-primary"
              //   onClick={() => handleOpenUploadModal(record)}
              // >
              //   {t("upload_proof")}
              // </Button>
              <CustomButton

                onClick={() => handleOpenUploadModal(record)}
              >
                <i className="ti ti-upload me-2"></i>
                {t("upload_proof")}
              </CustomButton>
            );
          }
          return "-";
        }

        return "-";
      },
    },
  ];

  return (
    <>
      <Table columns={columns.filter((col) => !col.hidden)} dataSource={absences} pagination={true} />

      {/* Upload Proof Modal */}
      {selectedAbsence && (
        <AbsenceProofUploadModal
          isOpen={uploadModalVisible}
          onClose={handleCloseUploadModal}
          absence={selectedAbsence}
          onSuccess={handleUploadSuccess}
        />
      )}
    </>
  );
};

export default AbsenceTable;
